import type { ApiError } from '@arpm/shared';

import { useAuthStore } from '@/stores/auth.js';

/**
 * Унифицированный API-клиент intranet-web.
 *
 * Поддерживает две формы вызова:
 *   1. apiRequest('/path', { method, body, headers }) — ручные вызовы из кода
 *   2. apiRequest('/path', { method, data, params, headers, signal }) — формат
 *      orval-сгенерированных хуков (см. src/api/generated/)
 *
 * Авторизация: Bearer-токен берётся из useAuthStore. ApiError разворачивается
 * единообразно — фронт всегда ловит ApiClientError с правильным statusCode.
 */

const baseUrl = import.meta.env.VITE_API_URL.replace(/\/$/, '');

export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly statusCode: number,
    public readonly apiError: ApiError | null,
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

type Primitive = string | number | boolean | null | undefined;
type ParamsRecord = Record<string, Primitive | Primitive[]>;

interface ExtendedInit extends Omit<RequestInit, 'body'> {
  /** orval отдаёт тело здесь; если задано — сериализуется в JSON body */
  data?: unknown;
  /** orval отдаёт query-параметры; собираем в querystring */
  params?: ParamsRecord;
  /** обратная совместимость с ручными вызовами */
  body?: BodyInit | null;
}

export async function apiRequest<T>(path: string, init: ExtendedInit = {}): Promise<T> {
  const authStore = useAuthStore();

  const url = appendParams(`${baseUrl}${path}`, init.params);
  const headers = new Headers(init.headers);
  if (authStore.accessToken) {
    headers.set('Authorization', `Bearer ${authStore.accessToken}`);
  }

  let body: BodyInit | null | undefined = init.body;
  if (body === undefined && init.data !== undefined) {
    if (init.data instanceof FormData) {
      body = init.data;
    } else {
      body = JSON.stringify(init.data);
      if (!headers.has('Content-Type')) {
        headers.set('Content-Type', 'application/json');
      }
    }
  } else if (body && !headers.has('Content-Type') && typeof body === 'string') {
    headers.set('Content-Type', 'application/json');
  }

  const response = await fetch(url, {
    method: init.method,
    headers,
    body: body ?? null,
    signal: init.signal,
    credentials: 'omit',
  });

  if (!response.ok) {
    let apiError: ApiError | null = null;
    try {
      apiError = (await response.json()) as ApiError;
    } catch {
      /* body не json */
    }
    throw new ApiClientError(
      apiError?.message ?? `${response.status} ${response.statusText}`,
      response.status,
      apiError,
    );
  }

  if (response.status === 204) {
    return undefined as T;
  }
  return (await response.json()) as T;
}

/**
 * Скачать бинарный ответ (например, выгрузку в Excel) с авторизацией.
 * `path` — как у сгенерированных `get…Url(params)`: уже с query-строкой.
 */
export async function apiBlob(path: string): Promise<Blob> {
  const authStore = useAuthStore();
  const headers = new Headers();
  if (authStore.accessToken) headers.set('Authorization', `Bearer ${authStore.accessToken}`);
  const response = await fetch(`${baseUrl}${path}`, { headers, credentials: 'omit' });
  if (!response.ok) {
    let apiError: ApiError | null = null;
    try {
      apiError = (await response.json()) as ApiError;
    } catch {
      /* body не json */
    }
    throw new ApiClientError(apiError?.message ?? `${response.status} ${response.statusText}`, response.status, apiError);
  }
  return response.blob();
}

/** Удобные шорткаты для ручных вызовов. */
export const apiClient = {
  get: <T>(path: string) => apiRequest<T>(path, { method: 'GET' }),
  post: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'POST', data }),
  put: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'PUT', data }),
  patch: <T>(path: string, data?: unknown) => apiRequest<T>(path, { method: 'PATCH', data }),
  delete: <T>(path: string) => apiRequest<T>(path, { method: 'DELETE' }),
};

function appendParams(url: string, params: ParamsRecord | undefined): string {
  if (!params) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) {
    if (value == null) continue;
    if (Array.isArray(value)) {
      for (const v of value) if (v != null) search.append(key, String(v));
    } else {
      search.append(key, String(value));
    }
  }
  const qs = search.toString();
  if (!qs) return url;
  return url + (url.includes('?') ? '&' : '?') + qs;
}
